import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Plus, Trash2, GraduationCap } from 'lucide-react';

// Assign teachers & surveillants (CPE) to sections.
// These assignments drive what Teacher/Surveillant-role users can
// see across the academic module: no assignment = no data.
// What they can DO comes from their role: Teachers enter grades only
// (a subject-bound row limits grading to that subject; "All subjects"
// = homeroom teacher grades everything); Surveillants record
// attendance & behavior for their sections (subject is ignored).
const TeacherAssignmentsModal = ({ apiCall, year, onClose, showSuccess, showError }) => {
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sections, setSections] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [filterTeacher, setFilterTeacher] = useState('');
  const [form, setForm] = useState({ teacherId: '', sectionId: '', subjectId: '' });

  const load = useCallback(async () => {
    try {
      const [asg, tchs, secs, cs] = await Promise.all([
        apiCall(`/academic/assignments?academicYear=${year}`),
        apiCall('/academic/teachers?scoped=1'),
        apiCall(`/academic/sections?academicYear=${year}`),
        apiCall(`/academic/grading/class-subjects?academicYear=${year}`)
      ]);
      setAssignments(asg);
      setTeachers(tchs);
      setSections(secs.filter(s => s.IsActive));
      setClassSubjects(cs);
    } catch (err) { showError(err.message); }
  }, [apiCall, year, showError]);

  useEffect(() => { load(); }, [load]);

  // Subjects offered = the top-level subjects actually taught in the
  // selected section's class (domains follow their parent subject)
  const sectionClass = useMemo(() =>
    sections.find(s => s.SectionID === parseInt(form.sectionId))?.ClassName || null,
    [sections, form.sectionId]);
  const classSubjectOptions = useMemo(() => {
    if (!sectionClass) return [];
    const seen = new Set();
    return classSubjects.filter(cs => {
      if (cs.ClassName !== sectionClass || cs.ParentClassSubjectID) return false;
      if (seen.has(cs.SubjectID)) return false;
      seen.add(cs.SubjectID);
      return true;
    });
  }, [classSubjects, sectionClass]);

  const visible = useMemo(() =>
    filterTeacher
      ? assignments.filter(a => a.UserID === parseInt(filterTeacher))
      : assignments,
    [assignments, filterTeacher]);

  // group rows per teacher for readability
  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of visible) {
      if (!map.has(a.UserID)) map.set(a.UserID, { name: a.TeacherName, rows: [] });
      map.get(a.UserID).rows.push(a);
    }
    return [...map.values()];
  }, [visible]);

  const handleAdd = async () => {
    if (!form.teacherId || !form.sectionId) { showError('Pick a teacher and a section'); return; }
    try {
      await apiCall('/academic/assignments', {
        method: 'POST',
        body: JSON.stringify({
          userId: parseInt(form.teacherId),
          academicYear: year,
          sectionId: parseInt(form.sectionId),
          subjectId: form.subjectId ? parseInt(form.subjectId) : null
        })
      });
      showSuccess('Assignment added');
      setForm(f => ({ ...f, sectionId: '', subjectId: '' }));
      await load();
    } catch (err) { showError(err.message); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Remove ${a.TeacherName} from ${a.ClassName} ${a.SectionCode}${a.SubjectName ? ` — ${a.SubjectName}` : ''}?`)) return;
    try {
      await apiCall(`/academic/assignments/${a.AssignmentID}`, { method: 'DELETE' });
      showSuccess('Assignment removed');
      await load();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[85vh] overflow-auto">
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <GraduationCap size={18} /> Teacher Assignments · {year}
          </h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Teacher &amp; Surveillant role users only see their assigned sections.
          Teachers: pick a subject to limit grading to it, or "All subjects" for a
          homeroom teacher. Surveillants (attendance &amp; behavior): subject is ignored.
        </p>

        {/* Add */}
        <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs text-gray-500 mb-1">Teacher</label>
            <select
              value={form.teacherId}
              onChange={e => setForm(f => ({ ...f, teacherId: e.target.value }))}
              className="w-full border rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">— teacher —</option>
              {teachers.map(t => (
                <option key={t.UserID} value={t.UserID}>
                  {t.FullName}{t.ScopedRoles ? ` (${t.ScopedRoles})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Section</label>
            <select
              value={form.sectionId}
              onChange={e => setForm(f => ({ ...f, sectionId: e.target.value, subjectId: '' }))}
              className="border rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">— section —</option>
              {sections.map(s => (
                <option key={s.SectionID} value={s.SectionID}>{s.ClassName} {s.SectionCode}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Subject{sectionClass ? ` (taught in ${sectionClass})` : ''}
            </label>
            <select
              value={form.subjectId}
              onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}
              disabled={!form.sectionId}
              className="border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">All subjects (homeroom / surveillant)</option>
              {classSubjectOptions.map(cs => (
                <option key={cs.SubjectID} value={cs.SubjectID}>{cs.SubjectName}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAdd}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs text-gray-500">Show:</label>
          <select
            value={filterTeacher}
            onChange={e => setFilterTeacher(e.target.value)}
            className="border rounded-lg px-2 py-1 text-sm"
          >
            <option value="">All teachers</option>
            {teachers.map(t => <option key={t.UserID} value={t.UserID}>{t.FullName}</option>)}
          </select>
        </div>

        {/* Assignments grouped per teacher */}
        <div className="space-y-3">
          {grouped.map(g => (
            <div key={g.name} className="border rounded-lg p-3">
              <p className="text-sm font-medium text-gray-800 mb-1.5">{g.name}</p>
              <div className="flex flex-wrap gap-1.5">
                {g.rows.map(a => (
                  <span key={a.AssignmentID}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                          a.SubjectID ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                    {a.ClassName} {a.SectionCode}{a.SubjectName ? ` · ${a.SubjectName}` : ' · Homeroom'}
                    <button onClick={() => handleDelete(a)} className="hover:text-red-600" title="Remove">
                      <Trash2 size={11} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}
          {grouped.length === 0 && (
            <p className="text-sm text-gray-500 italic text-center py-3">
              No teacher assignments yet for {year}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherAssignmentsModal;
